import React, { useCallback } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { DateDisplay, Table, Tooltip } from '@bringg/react-components';
import { Run, Task } from '@bringg/types';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';

import { getLocalizedDistanceMessage } from 'bringg-web/utils/distance';
import useStores from '../../recipes/use-stores';
import useTimeFormat from '../../hooks/use-time-format';

import './runs-list.scss';

interface Props {
	selectedRunIds: number[];
	selectedRunTasks?: Task[];
	sourceRun?: Run;
	sourceTasks?: Task[];
	runs: Run[];
	onRunSelected?: (runIds: number[]) => void;
	isSelectable?: boolean;
	selectedTasksWeight: { maxWeight: number; weightCapacity: number };
}

const RunsList: React.FC<Props> = ({
	selectedRunIds,
	selectedRunTasks,
	sourceRun,
	sourceTasks,
	runs,
	onRunSelected,
	isSelectable,
	selectedTasksWeight
}: Props) => {
	const { vehiclesStore, vehicleTypesStore, teamsStore, merchantConfigurationsStore } = useStores();
	const { t } = useTranslation();
	const timeFormat = useTimeFormat();

	const emptyCell = '-';

	const columns = [
		{
			key: 'routeName',
			render: (run: Run) => {
				const name = run.route_name || run.external_id || run.id;

				if (!name) {
					return '';
				}

				return (
					<span
						className={classNames('route-name', { 'route-name-self': isSourceRun(run.id) })}
						data-test-id="route-name"
					>
						# {name}
					</span>
				);
			}
		},
		{
			key: 'taskCount',
			render: (run: Run) => (
				<span className="tasks-count" data-test-id="tasks-count">
					<Tooltip
						key="tasks-count-tooltip"
						title={t('RUN_USER_ASSIGNMENT.ORDERS')}
						placement="top"
						overlayClassName="runs-tooltip"
					>
						<BringgIcon iconName={BringgFontIcons.List} className="icon-with-text" />
					</Tooltip>

					{run.task_ids.length}
				</span>
			)
		},
		{
			key: 'scheduledTime',
			render: (run: Run) => {
				const teamTimezone = teamsStore.get(run.team_id)?.localizationTimezone;

				return (
					<span className="scheduled-time">
						<Tooltip
							key="scheduled-time-tooltip"
							title={t('RUN_USER_ASSIGNMENT.DELIVERY_WINDOW')}
							placement="top"
							overlayClassName="runs-tooltip"
						>
							<BringgIcon iconName={BringgFontIcons.Clock} className="icon-with-text" />
						</Tooltip>
						<DateDisplay
							date={run.scheduled_start_time}
							format={`MM/DD ${timeFormat}`}
							timezone={teamTimezone}
						/>
						-
						<DateDisplay date={run.scheduled_end_time} format={timeFormat} timezone={teamTimezone} />
					</span>
				);
			}
		},
		{
			key: 'plannedDistance',
			render: (run: Run) => {
				return (
					<span className="planned-distance">
						<Tooltip
							key="planned-distance-tooltip"
							title={t('RUN_USER_ASSIGNMENT.DISTANCE')}
							placement="top"
							overlayClassName="runs-tooltip"
						>
							<BringgIcon iconName={BringgFontIcons.Distance} className="icon-with-text" />
						</Tooltip>

						{run.planned_transport_distance
							? getLocalizedDistanceMessage(
									run.planned_transport_distance * 1000,
									merchantConfigurationsStore.merchantCountry,
									t
							  )
							: emptyCell}
					</span>
				);
			}
		},
		{
			key: 'capacity',
			render: (run: Run) => {
				let weightData;
				const tasks = run.id === sourceRun?.id ? selectedRunTasks : sourceTasks;
				if (tasks && selectedTasksWeight && run.id !== sourceRun?.id) {
					weightData = { value: selectedTasksWeight.maxWeight, maxValue: selectedTasksWeight.weightCapacity };
				} else if (run?.weight_capacity) {
					weightData = { value: run.max_weight, maxValue: run.weight_capacity };
				} else if (selectedTasksWeight) {
					weightData = { value: selectedTasksWeight.maxWeight, maxValue: selectedTasksWeight.weightCapacity };
				}

				if (!weightData) return null;

				return (
					<span className="run-capacity">
						<Tooltip
							key="capacity-tooltip"
							title={t('RUN_USER_ASSIGNMENT.CAPACITY')}
							placement="top"
							overlayClassName="runs-tooltip"
						>
							<BringgIcon iconName={BringgFontIcons.Weight} className="icon-with-text" />
						</Tooltip>

						{`${Math.round(weightData.value * 100) / 100}/${Math.round(weightData.maxValue * 100) / 100}`}
					</span>
				);
			}
		},
		{
			key: 'vehicleType',
			render: (run: Run) => {
				const vehicleTypeTitle = getVehicleTypeTitle(run);
				const enableVehicles = merchantConfigurationsStore.configuration.enable_vehicles;

				if (!enableVehicles) return null;

				return (
					<span className="vehicle-type">
						<Tooltip
							key="vehicle-type-tooltip"
							title={t('RUN_USER_ASSIGNMENT.VEHICLE')}
							placement="top"
							overlayClassName="runs-tooltip"
						>
							<BringgIcon iconName={BringgFontIcons.Truck} className="icon-with-text" />
						</Tooltip>

						{vehicleTypeTitle || emptyCell}
					</span>
				);
			}
		}
	];

	const getVehicleTypeTitle = useCallback(run => {
		if (!run.vehicle_id) {
			return '';
		}

		const vehicle = vehiclesStore.get(run.vehicle_id);
		const vehicleType = vehicleTypesStore.get(vehicle?.vehicle_type_id);

		return vehicleType?.title || '';
	}, []);

	const selectRun = runIds => {
		if (!isSelectable) {
			return;
		}
		if (!isSourceRun(runIds[0])) {
			onRunSelected(runIds);
		}
	};

	const isSourceRun = runId => {
		return sourceRun && runId === sourceRun.id;
	};

	return (
		<Table
			rowKey="id"
			className="runs-list"
			showHeader={false}
			columns={columns}
			dataSource={runs}
			rowSelection={
				selectedRunIds &&
				isSelectable && {
					selectedRowKeys: selectedRunIds,
					onChange: selectRun,
					type: 'radio'
				}
			}
			rowClassName={isSelectable ? '' : 'not-selectable'}
			onRow={
				selectedRunIds &&
				(run => ({
					onClick: () => {
						selectRun([run.id]);
					}
				}))
			}
		/>
	);
};

export default RunsList;
